import React, {useEffect, useMemo, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import type {MapRef} from "react-map-gl/mapbox";
import MapGL, {FullscreenControl, Layer, NavigationControl, Popup, Source} from "react-map-gl/mapbox";
import {CountryElement} from "../../../common/uiElements/CountryElement";
import {countryName} from "../../components/countryLabels";
import {useInsightsFormatters} from "../../kit/insightsFormat";
import "mapbox-gl/dist/mapbox-gl.css";

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

/** How long the start-up skeleton may cover the map before it gives up waiting for `load`. */
const MAP_START_UP_LIMIT = 10_000;

const INITIAL_ZOOM = 5;
const AUSTRIA_LOCATION = {longitude: 14.5501, latitude: 47.5162};
const COUNTRY_WORLDVIEW = "US";

// org theme colors can be 8-digit hex (#RRGGBBAA); mapbox only accepts 6-digit / rgb / named.
function toMapboxColor(color: string): string {
    return /^#[0-9a-fA-F]{8}$/.test(color) ? color.slice(0, 7) : color;
}

function buildLayers(color: string, hoveredPointId?: string) {
    const pointHovered = ["==", ["get", "pointId"], hoveredPointId ?? ""];

    return {
        pointGlow: {
            id: "guest-points-glow",
            type: "circle",
            paint: {
                "circle-color": color,
                "circle-opacity": ["case", pointHovered, 0.22, 0.12],
                "circle-blur": 0.6,
                "circle-radius": ["case", pointHovered, 20, 8],
                "circle-radius-transition": {duration: 140},
                "circle-opacity-transition": {duration: 140},
            },
        } as any,
        pointCore: {
            id: "guest-point",
            type: "circle",
            paint: {
                "circle-color": color,
                "circle-radius": ["case", pointHovered, 9, 4],
                "circle-radius-transition": {duration: 140},
                "circle-stroke-width": ["case", pointHovered, 3, 1.5],
                "circle-stroke-width-transition": {duration: 140},
                "circle-stroke-color": "#ffffff",
            },
        } as any,
    };
}

type PopupAnchor = "top-left" | "top-right" | "bottom-left" | "bottom-right";

interface HoveredMapFeatureBase {
    longitude: number;
    latitude: number;
    popupAnchor: PopupAnchor;
    countryCode: string;
    guests: number;
    compareGuests?: number;
}

interface HoveredCityFeature extends HoveredMapFeatureBase {
    kind: "city";
    city: string;
    pointId: string;
}

interface HoveredCountryFeature extends HoveredMapFeatureBase {
    kind: "country";
    tone?: GuestMapCountryValue["tone"];
}

type HoveredMapFeature = HoveredCityFeature | HoveredCountryFeature;

function MapMessage({children}: { children: React.ReactNode }) {
    return (
        <Stack alignItems={"center"} justifyContent={"center"}
               sx={{height: "100%", px: 3, textAlign: "center", bgcolor: "action.hover"}}>
            <Typography variant={"body2"} color={"text.secondary"}>
                {children}
            </Typography>
        </Stack>
    );
}

interface GuestMapProps {
    points: GuestMapPoint[];
    loading: boolean;
    height?: number;
    highlightCountries?: string[];
    countryValues?: GuestMapCountryValue[];
    selectedPeriod?: GuestMapPeriod;
    comparisonPeriod?: GuestMapPeriod;
    fullscreenContainerId?: string;
    propertyLocation?: GuestMapLocation;
}

export interface GuestMapPeriod {
    label: string;
}

export interface GuestMapCountryValue {
    countryCode: string;
    selectedGuests: number;
    comparisonGuests?: number;
    tone?: "success" | "warning" | "error";
}

export interface GuestMapLocation {
    longitude: number;
    latitude: number;
}

export interface GuestMapPoint {
    city: string;
    countryCode: string;
    lat: number;
    lng: number;
    guests: number;
    compareGuests?: number;
}

const EMPTY_COUNTRY_CODES: string[] = [];
const EMPTY_COUNTRY_VALUES: GuestMapCountryValue[] = [];

function popupAnchorForPoint(x: number, y: number, width: number, height: number): PopupAnchor {
    if (y > height / 2) {
        return x > width / 2 ? "bottom-right" : "bottom-left";
    }
    return x > width / 2 ? "top-right" : "top-left";
}

function isValidLocation(location?: GuestMapLocation): location is GuestMapLocation {
    return location != null
        && Number.isFinite(location.longitude)
        && Number.isFinite(location.latitude)
        && Math.abs(location.longitude) <= 180
        && Math.abs(location.latitude) <= 90;
}

/** Guest-location map: one compact point per resolved city, with totals inspected through pointer-following tooltips. */
function GuestMap({
                      points,
                      loading,
                      height = 800,
                      highlightCountries = EMPTY_COUNTRY_CODES,
                      countryValues = EMPTY_COUNTRY_VALUES,
                      selectedPeriod,
                      comparisonPeriod,
                      fullscreenContainerId,
                      propertyLocation,
                  }: GuestMapProps) {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const [hovered, setHovered] = useState<HoveredMapFeature | null>(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const mapRef = useRef<MapRef>(null);
    const countryHoverFrame = useRef<number | null>(null);
    const pendingCountryHover = useRef<HoveredCountryFeature | null>(null);

    const cancelPendingCountryHover = () => {
        if (countryHoverFrame.current != null) {
            window.cancelAnimationFrame(countryHoverFrame.current);
            countryHoverFrame.current = null;
        }
        pendingCountryHover.current = null;
    };

    const clearHover = () => {
        cancelPendingCountryHover();
        setHovered(null);
    };

    const queueCountryHover = (nextHovered: HoveredCountryFeature) => {
        pendingCountryHover.current = nextHovered;
        if (countryHoverFrame.current != null) return;

        countryHoverFrame.current = window.requestAnimationFrame(() => {
            countryHoverFrame.current = null;
            const next = pendingCountryHover.current;
            if (next == null) return;

            pendingCountryHover.current = null;
            setHovered(current => current?.kind === "country" && current.countryCode === next.countryCode
                ? {...next, popupAnchor: current.popupAnchor}
                : next);
        });
    };

    useEffect(() => {
        pendingCountryHover.current = null;
        if (countryHoverFrame.current != null) {
            window.cancelAnimationFrame(countryHoverFrame.current);
            countryHoverFrame.current = null;
        }
        setHovered(null);

        return () => {
            if (countryHoverFrame.current != null) {
                window.cancelAnimationFrame(countryHoverFrame.current);
            }
        };
    }, [countryValues, points]);

    const activeLocation = isValidLocation(propertyLocation) ? propertyLocation : AUSTRIA_LOCATION;
    const initialViewState = useMemo(() => ({
        longitude: activeLocation.longitude,
        latitude: activeLocation.latitude,
        zoom: INITIAL_ZOOM,
    }), [activeLocation.latitude, activeLocation.longitude]);

    useEffect(() => {
        if (loading) setMapLoaded(false);
    }, [loading]);

    /*
     * `load` never arriving is a real failure - a rejected style, no network, a tab that never got a frame
     * to render in. Whatever mapbox managed to paint beats a skeleton that never goes away.
     */
    useEffect(() => {
        if (loading || mapLoaded) return;

        const timer = window.setTimeout(() => setMapLoaded(true), MAP_START_UP_LIMIT);

        return () => window.clearTimeout(timer);
    }, [loading, mapLoaded]);

    useEffect(() => {
        if (!mapLoaded) return;
        mapRef.current?.easeTo({
            center: [activeLocation.longitude, activeLocation.latitude],
            zoom: INITIAL_ZOOM,
            duration: 500,
        });
    }, [activeLocation.latitude, activeLocation.longitude, mapLoaded]);

    const geojson = useMemo<GeoJSON.FeatureCollection>(() => ({
        type: "FeatureCollection",
        features: points
            .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng))
            .map((p, index) => ({
                type: "Feature",
                geometry: {type: "Point", coordinates: [p.lng, p.lat]},
                properties: {
                    pointId: `${p.countryCode}|${p.city}|${index}`,
                    city: p.city,
                    countryCode: p.countryCode,
                    guests: p.guests,
                    compareGuests: p.compareGuests,
                },
            })),
    }), [points]);

    const hoveredPointId = hovered?.kind === "city" ? hovered.pointId : undefined;
    const layers = useMemo(() => buildLayers(
        toMapboxColor(theme.palette.primary.main),
        hoveredPointId,
    ), [hoveredPointId, theme.palette.primary.main]);
    const locale = i18n.resolvedLanguage || i18n.language;
    const {count, percent} = useInsightsFormatters();

    /*
        The change against the comparison period, as a count and as a share of it. The share is what
        the map is already coloured by - the legend speaks in percentages ("Up to 20% fewer") while
        the card only ever showed a count, so the number behind the colour was never readable.

        A country with no guests in the comparison period has no share to state: everything is an
        increase from nothing, and "+12 guests (+100 %)" would be a made-up figure.
    */
    const formatGuestDifference = (guests: number, compareGuests: number) => {
        const difference = guests - compareGuests;
        const signed = `${difference > 0 ? "+" : ""}${count(difference)} ${t("guests")}`;

        if (compareGuests === 0) return signed;

        return `${signed} (${difference > 0 ? "+" : ""}${percent(difference * 100 / compareGuests)})`;
    };
    const normalizedHighlightCountries = useMemo(
        () => highlightCountries.map(countryCode => countryCode.toUpperCase()),
        [highlightCountries],
    );
    const normalizedCountryValues = useMemo(() => countryValues.map(value => ({
        ...value,
        countryCode: value.countryCode.toUpperCase(),
    })), [countryValues]);
    const countryValueByCode = useMemo(() => new Map(
        normalizedCountryValues.map(value => [value.countryCode, value] as const),
    ), [normalizedCountryValues]);
    const countryValueCodes = useMemo(
        () => normalizedCountryValues.map(value => value.countryCode),
        [normalizedCountryValues],
    );
    const hoveredCountryCode = hovered?.countryCode.toUpperCase();
    const comparing = selectedPeriod != null && comparisonPeriod != null;
    const countryValueColor = useMemo(() => [
        "match",
        ["get", "iso_3166_1"],
        ...normalizedCountryValues.flatMap(value => [
            value.countryCode,
            toMapboxColor(value.tone ? theme.palette[value.tone].main : theme.palette.primary.main),
        ]),
        "rgba(0, 0, 0, 0)",
    ], [normalizedCountryValues, theme.palette]);
    const countryBoundaryFilter = useMemo(() => [
        "all",
        ["==", ["get", "disputed"], "false"],
        [
            "any",
            ["==", ["get", "worldview"], "all"],
            ["in", COUNTRY_WORLDVIEW, ["get", "worldview"]],
        ],
        ["in", ["get", "iso_3166_1"], ["literal", countryValueCodes]],
    ], [countryValueCodes]);
    const countryResultLayers = useMemo(() => ({
        fill: {
            id: "country-result-fill",
            type: "fill",
            slot: "middle",
            source: "country-bounds",
            "source-layer": "country_boundaries",
            filter: countryBoundaryFilter,
            paint: {
                "fill-color": countryValueColor,
                "fill-opacity": [
                    "case",
                    ["==", ["get", "iso_3166_1"], hoveredCountryCode ?? ""],
                    comparing ? 0.28 : 0.16,
                    comparing ? 0.14 : 0.001,
                ],
            },
        },
        line: {
            id: "country-result-line",
            type: "line",
            slot: "middle",
            source: "country-bounds",
            "source-layer": "country_boundaries",
            filter: countryBoundaryFilter,
            paint: {
                "line-color": countryValueColor,
                "line-width": [
                    "case",
                    ["==", ["get", "iso_3166_1"], hoveredCountryCode ?? ""],
                    comparing ? 4 : 2.5,
                    comparing ? 2.5 : 1,
                ],
                "line-opacity": [
                    "case",
                    ["==", ["get", "iso_3166_1"], hoveredCountryCode ?? ""],
                    comparing ? 0.95 : 0.8,
                    comparing ? 0.95 : 0,
                ],
            },
        },
    }), [comparing, countryBoundaryFilter, countryValueColor, hoveredCountryCode]);

    const inner = () => {
        if (!MAPBOX_TOKEN) {
            return <MapMessage>{t("Add REACT_APP_MAPBOX_TOKEN to your .env and restart the dev server to load the map.")}</MapMessage>;
        }
        if (loading) {
            return <Skeleton variant={"rectangular"} sx={{width: "100%", height: "100%"}}/>;
        }
        if (geojson.features.length === 0) {
            return <MapMessage>{t("No geocoded guest cities yet.")}</MapMessage>;
        }
        return (
            <React.Fragment>
            {/*
              * Mapbox paints its canvas white until the style and the first tiles are in, which on a cold
              * start is seconds of a blank rectangle. The skeleton stays over it until `onLoad` says the
              * map has something to show.
              */}
            {!mapLoaded && (
                <Skeleton
                    variant={"rectangular"}
                    sx={{position: "absolute", inset: 0, width: "100%", height: "100%", zIndex: 5}}
                />
            )}
            <MapGL
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={initialViewState}
                mapStyle={"mapbox://styles/mapbox/light-v11"}
                cooperativeGestures={true}
                style={{width: "100%", height: "100%"}}
                onLoad={() => setMapLoaded(true)}
                onError={() => setMapLoaded(true)}
                interactiveLayerIds={countryValues.length > 0
                    ? ["guest-point", "country-result-fill"]
                    : ["guest-point"]}
                onMouseMove={(e) => {
                    const feature = e.features?.find(item => item.layer?.id === "guest-point")
                        ?? e.features?.find(item => item.layer?.id === "country-result-fill");
                    const layerId = feature?.layer?.id;
                    if (!feature?.properties || !layerId) {
                        clearHover();
                        return;
                    }

                    const canvas = e.target.getCanvas();
                    const popupAnchor = popupAnchorForPoint(e.point.x, e.point.y, canvas.clientWidth, canvas.clientHeight);

                    if (layerId === "country-result-fill") {
                        const countryCode = String(feature.properties.iso_3166_1 ?? "").toUpperCase();
                        const country = countryValueByCode.get(countryCode);
                        if (!country) {
                            clearHover();
                            return;
                        }
                        queueCountryHover({
                            kind: "country",
                            longitude: e.lngLat.lng,
                            latitude: e.lngLat.lat,
                            popupAnchor,
                            countryCode,
                            guests: country.selectedGuests,
                            compareGuests: country.comparisonGuests,
                            tone: country.tone,
                        });
                        return;
                    }

                    cancelPendingCountryHover();
                    const coordinates = (feature.geometry as GeoJSON.Point).coordinates;
                    const pointId = String(feature.properties.pointId);
                    const nextHovered: HoveredCityFeature = {
                        kind: "city",
                        longitude: coordinates[0],
                        latitude: coordinates[1],
                        popupAnchor,
                        city: String(feature.properties.city),
                        countryCode: String(feature.properties.countryCode).toUpperCase(),
                        guests: Number(feature.properties.guests),
                        compareGuests: feature.properties.compareGuests == null
                            ? undefined
                            : Number(feature.properties.compareGuests),
                        pointId,
                    };
                    setHovered(current => current?.kind === "city"
                    && current.pointId === pointId
                    && current.popupAnchor === popupAnchor
                        ? current
                        : nextHovered);
                }}
                onMouseLeave={clearHover}
            >
                <FullscreenControl position={"top-right"} containerId={fullscreenContainerId}/>
                <NavigationControl position={"top-right"} showCompass={false}/>

                {(countryValues.length > 0 || normalizedHighlightCountries.length > 0) && (
                    <Source id={"country-bounds"} type={"vector"} url={"mapbox://mapbox.country-boundaries-v1"}>
                        {countryValues.length > 0 && (
                            <>
                                <Layer {...(countryResultLayers.fill as any)}/>
                                <Layer {...(countryResultLayers.line as any)}/>
                            </>
                        )}
                        {normalizedHighlightCountries.length > 0 && (
                            <>
                                <Layer {...({
                                    id: "country-fill",
                                    type: "fill",
                                    source: "country-bounds",
                                    "source-layer": "country_boundaries",
                                    filter: ["in", ["get", "iso_3166_1"], ["literal", normalizedHighlightCountries]],
                                    paint: {
                                        "fill-color": toMapboxColor(theme.palette.primary.main),
                                        "fill-opacity": 0.1
                                    },
                                } as any)}/>
                                <Layer {...({
                                    id: "country-line",
                                    type: "line",
                                    source: "country-bounds",
                                    "source-layer": "country_boundaries",
                                    filter: ["in", ["get", "iso_3166_1"], ["literal", normalizedHighlightCountries]],
                                    paint: {
                                        "line-color": toMapboxColor(theme.palette.primary.main),
                                        "line-width": 1.5,
                                        "line-opacity": 0.6
                                    },
                                } as any)}/>
                            </>
                        )}
                    </Source>
                )}

                <Source
                    id={"guests"}
                    type={"geojson"}
                    data={geojson}
                >
                    <Layer {...layers.pointGlow}/>
                    <Layer {...layers.pointCore}/>
                </Source>

                {hovered && (
                    <Popup
                        key={hovered.popupAnchor}
                        longitude={hovered.longitude}
                        latitude={hovered.latitude}
                        closeButton={false}
                        closeOnClick={false}
                        anchor={hovered.popupAnchor}
                        offset={12}
                        maxWidth="320px"
                        className="guest-map-hover-tooltip"
                    >
                        <Stack sx={{minWidth: comparisonPeriod ? 220 : 150, maxWidth: 280}}>
                            <Stack direction="row" alignItems="center" gap={0.75} sx={{minWidth: 0}}>
                                <CountryElement countryCode={hovered.countryCode} shape="flag"/>
                                <Typography
                                    component="div"
                                    variant="body1"
                                    lineHeight={1.3}
                                    fontWeight={700}
                                    color="text.primary"
                                    noWrap
                                    sx={{minWidth: 0, overflow: "hidden", textOverflow: "ellipsis"}}
                                >
                                    {hovered.kind === "country"
                                        ? countryName(hovered.countryCode, locale)
                                        : `${countryName(hovered.countryCode, locale)} · ${hovered.city}`}
                                </Typography>
                            </Stack>
                            {comparisonPeriod && selectedPeriod ? (
                                <Stack gap={0.75} sx={{mt: 1}}>
                                    {[
                                        {
                                            period: selectedPeriod,
                                            guests: hovered.guests,
                                        },
                                        {
                                            period: comparisonPeriod,
                                            guests: hovered.compareGuests ?? 0,
                                        },
                                    ].map(item => (
                                        <Stack key={item.period.label} direction="row" alignItems="center"
                                               justifyContent="space-between" gap={2}>
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                {item.period.label}
                                            </Typography>
                                            <Typography variant="body2" fontWeight={700} color="text.primary" noWrap>
                                                {item.guests.toLocaleString(locale)} {t("guests")}
                                            </Typography>
                                        </Stack>
                                    ))}
                                    {hovered.kind === "country" && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={2}
                                            sx={{pt: 0.75, borderTop: "1px solid", borderColor: "divider"}}
                                        >
                                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                                {t("Difference")}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={800}
                                                color={hovered.tone ? `${hovered.tone}.main` : "text.primary"}
                                                noWrap
                                            >
                                                {formatGuestDifference(hovered.guests, hovered.compareGuests ?? 0)}
                                            </Typography>
                                        </Stack>
                                    )}
                                </Stack>
                            ) : (
                                <Typography variant="body2" fontWeight={700} color="text.primary" sx={{mt: 1}}>
                                    {hovered.guests.toLocaleString(locale)} {t("guests")}
                                </Typography>
                            )}
                        </Stack>
                    </Popup>
                )}
            </MapGL>
            </React.Fragment>
        );
    };

    return (
        <Box
            className="guest-map-root"
            onPointerLeave={clearHover}
            sx={{
                height,
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                "& .guest-map-hover-tooltip, & .guest-map-hover-tooltip *": {
                    pointerEvents: "none !important",
                },
                "& .guest-map-hover-tooltip .mapboxgl-popup-content": {
                    p: 1.25,
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    boxShadow: theme.shadows[4],
                },
                "& .guest-map-hover-tooltip.mapboxgl-popup-anchor-bottom-left .mapboxgl-popup-tip, & .guest-map-hover-tooltip.mapboxgl-popup-anchor-bottom-right .mapboxgl-popup-tip": {
                    borderTopColor: theme.palette.background.paper,
                },
                "& .guest-map-hover-tooltip.mapboxgl-popup-anchor-top-left .mapboxgl-popup-tip, & .guest-map-hover-tooltip.mapboxgl-popup-anchor-top-right .mapboxgl-popup-tip": {
                    borderBottomColor: theme.palette.background.paper,
                },
            }}
        >
            {inner()}
        </Box>
    );
}

export default GuestMap;
